import React, { useState, useEffect, Fragment } from 'react';
import {
    Button, Drawer, DrawerBody, SearchInput,
} from '@paretogroup/tess';
import { SegmentsFilterIcon } from '@domain/segments/segments-drawer/icon/segments-filter-icon';
import smartViewTexts from '@shared/i18n/overview/texts';
import { withStyles } from '@material-ui/core';
import { OptionWithEditDeleteIcon } from '@domain/segments/segments-drawer/option-with-edit-delete-icon/option-with-edit-delete-icon';
import Loading from '@shared/components/loading/Loading';
import { ColorConstants } from '@shared/constants';
import {
    ContainerBody,
    ContainerContent,
    ContainerScrollBar,
    InputSearchContainer,
    Title,
    useStyles,
    DrawerFieldContainer,
} from './segments-drawer.styles';
import { SegmentsHeader } from './header/segments-header';
import { useCountSegments } from './segments-drawer.hooks';
import { OptionProps } from './segments-drawer.types';
import { APPCUES_ID } from './segments-drawer.constants';

interface SegmentsDrawerProps {
    loggedUser: {
        'user_img_path': string;
    };
    buttonLabel?: string;
    classes?: Partial<Record<string, string>>;
    handleGroupChange?: (e: any) => void;
    isDesktop?: boolean;
    isLoading?: boolean;
    onClickButton?: (e: any) => void;
    onEditTagsGroup?: (e: any) => void;
    onOpenDeleteGroupModal?: (e: any) => void;
    options?: OptionProps[];
    secondaryColor?: string;
    size?: number;
}

const defaultProps = {
    loggedUser: {
        user_img_path: '',
    },
    buttonLabel: '',
    children: null,
    isDesktop: false,
    isLoading: false,
    options: [],
    styles: {},
    subtitle: '',
    title: null,
};

const SegmentsDrawerImpl = ({
    buttonLabel,
    classes,
    handleGroupChange,
    isDesktop,
    isLoading,
    loggedUser,
    onClickButton,
    onEditTagsGroup,
    onOpenDeleteGroupModal,
    options,
    secondaryColor,
    size,
}: SegmentsDrawerProps) => {
    const [isOpen, toggleOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [segmentSelected, setSegmentSelected] = useState<OptionProps>({});
    const countSegments = useCountSegments(options);
    const [isHover, setIsHover] = useState(false);

    const applyFilter = () => {
        if (filter && filter !== '') {
            return Object.values(options)
                .filter(option => option.label.toLowerCase()
                    .indexOf(filter.toLowerCase()) >= 0);
        }
        return options;
    };

    useEffect(() => {
        if (!isOpen) setFilter('');
    }, [isOpen]);

    useEffect(() => {
        const getTheSelectedSegment = options.filter(option => option.checked);
        if (getTheSelectedSegment[0] !== null) { setSegmentSelected(getTheSelectedSegment[0]); }
    }, [options]);

    const { user_img_path: userImgPath } = loggedUser;

    return (
        <Fragment>
            <SegmentsFilterIcon
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                onClick={() => toggleOpen(!isOpen)}
                secondaryColor={isHover ? ColorConstants.FUN_LAVANDER_DARK : secondaryColor}
                size={size}
                isDesktop={isDesktop}
                data-appcues-id={APPCUES_ID.CLICKABLE_FILTER_ICON}
            />
            <Drawer
                onClose={() => toggleOpen(false)}
                open={isOpen}
                /* Prop needed to correct the error:
                `Uncaught RangeError: Maximum call stack size exceeded.`
                this problem happened to any modal that was opened while Drawer was open
                ref: https://stackoverflow.com/questions/54133326/material-ui-uncaught-rangeerror-maximum-call-stack-size-exceeded */
                disableEnforceFocus
            >
                <SegmentsHeader
                    headerTitle={smartViewTexts.tagsGroup.groupBy}
                    headerSubtitle={`You got ${countSegments.segmentsByPareto} segments created by Pareto${countSegments.segmentsByTeam}.`}
                    isLoading={isLoading}
                    segmentSelected={segmentSelected}
                    userImage={userImgPath}
                />
                <DrawerBody className={classes.drawerBody}>
                    <Title>Search segments</Title>
                    <ContainerBody>
                        <InputSearchContainer>
                            <SearchInput
                                placeholder="Search..."
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                                type="search"
                            />
                        </InputSearchContainer>
                        <ContainerContent>
                            <ContainerScrollBar>
                                {isLoading ? <Loading />
                                    : Object.values(applyFilter()).map(option => (
                                        <DrawerFieldContainer key={option.value}>
                                            <OptionWithEditDeleteIcon
                                                label={option.label}
                                                value={option.value}
                                                checked={option.checked}
                                                onDelete={onOpenDeleteGroupModal}
                                                onEdit={onEditTagsGroup}
                                                onClick={handleGroupChange}
                                                isDeletable={option.isDeletable}
                                                isEditable={option.isEditable}
                                            />
                                        </DrawerFieldContainer>
                                    ))}
                            </ContainerScrollBar>
                            <Button
                                size="medium"
                                onClick={onClickButton}
                                classes={{ label: classes.buttonText }}
                            >
                                {buttonLabel}
                            </Button>
                        </ContainerContent>
                    </ContainerBody>
                </DrawerBody>
            </Drawer>
        </Fragment>

    );
};

SegmentsDrawerImpl.defaultProps = defaultProps;

export const SegmentsDrawer = withStyles(useStyles)(SegmentsDrawerImpl);
